import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { api, ApiError, onUnauthorized } from "./api";

// Authentication state never blocks rendering: "checking" shows a visible loading screen, API failures
// become an explicit "api_error" state with a retry button, and 401 means "anonymous" (login page).
export interface Me {
  id: number;
  username: string;
  role: string;
  is_owner: boolean;
  permissions: string[];
}

type AuthState =
  | { status: "checking" }
  | ({ status: "authenticated" } & Me)
  | { status: "anonymous" }
  | { status: "api_error"; message: string };

interface AuthCtx {
  state: AuthState;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  recheck: () => void;
}

const Ctx = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: "checking" });

  const recheck = useCallback(() => {
    setState({ status: "checking" });
    api
      .get<Me>("/api/auth/me", 10000)
      .then((u) => setState({ status: "authenticated", ...u }))
      .catch((e: ApiError) => {
        if (e.status === 401) setState({ status: "anonymous" });
        else setState({ status: "api_error", message: e.message });
      });
  }, []);

  useEffect(() => {
    recheck();
    return onUnauthorized(() => setState({ status: "anonymous" }));
  }, [recheck]);

  const login = useCallback(async (username: string, password: string) => {
    const u = await api.post<Me>("/api/auth/login", { username, password });
    setState({ status: "authenticated", ...u });
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/api/auth/logout");
    } finally {
      setState({ status: "anonymous" });
    }
  }, []);

  const value = useMemo(() => ({ state, login, logout, recheck }), [state, login, logout, recheck]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAuth outside AuthProvider");
  return v;
}

/** Permission check for showing UI; the API enforces the same rules. */
export function useCan(): (perm: string) => boolean {
  const { state } = useAuth();
  const perms = state.status === "authenticated" ? state.permissions : null;
  return useCallback((perm: string) => !!perms?.includes(perm), [perms]);
}
